// Toast Notifications - Enhanced MD3 Implementation

const DEFAULT_TOAST_DURATION = 5000;
const TOAST_ICONS = Object.freeze({
    success: 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z',
    error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM10 6a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z',
    info: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
    warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
});

let toastContainer = null;
let legacyToastTimeout = null;

/**
 * Initialize toast container if it doesn't exist
 */
function ensureToastContainer() {
    if (!toastContainer) {
        toastContainer = document.getElementById('toast-container');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.id = 'toast-container';
            toastContainer.className = 'toast-container';
            // Add aria-live for screen reader announcements
            toastContainer.setAttribute('aria-live', 'polite');
            toastContainer.setAttribute('aria-atomic', 'false');
            toastContainer.setAttribute('role', 'region');
            toastContainer.setAttribute('aria-label', 'Notifications');
            document.body.appendChild(toastContainer);
        }
    }
    return toastContainer;
}

/**
 * Create SVG icon element
 */
function createToastIcon(type) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('class', 'toast-icon-svg');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute('clip-rule', 'evenodd');
    path.setAttribute('d', TOAST_ICONS[type] || TOAST_ICONS.info);

    svg.appendChild(path);
    return svg;
}

/**
 * Show toast notification
 * @param {string} message - The message to display
 * @param {string} type - Toast type: 'success', 'error', 'info', 'warning'
 * @param {Object} options - Additional options
 * @param {number} options.duration - Duration in ms (default: 5000)
 * @param {string} options.action - Action button text
 * @param {Function} options.onAction - Action button callback
 * @param {boolean} options.persistent - If true, won't auto-dismiss
 */
export function showToast(message, type = 'info', options = {}) {
    const {
        duration = DEFAULT_TOAST_DURATION,
        action = null,
        onAction = null,
        persistent = false
    } = options;

    // Fallback to legacy toast element if it exists and is not enhanced
    const legacyToast = document.getElementById('toast');
    if (legacyToast && !legacyToast.classList.contains('toast-enhanced')) {
        const messageEl = legacyToast.querySelector('.toast-message');
        const iconEl = legacyToast.querySelector('.toast-icon');

        if (messageEl) {
            messageEl.textContent = message;
        } else {
            legacyToast.textContent = message;
        }

        legacyToast.className = `toast ${type} show`;

        if (iconEl) {
            while (iconEl.firstChild) {
                iconEl.removeChild(iconEl.firstChild);
            }
            const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pathEl.setAttribute('fill-rule', 'evenodd');
            pathEl.setAttribute('clip-rule', 'evenodd');
            pathEl.setAttribute('d', TOAST_ICONS[type] || TOAST_ICONS.info);
            iconEl.appendChild(pathEl);
        }

        clearTimeout(legacyToastTimeout);
        legacyToastTimeout = setTimeout(() => {
            legacyToast.classList.remove('show');
        }, duration);

        return { dismiss: () => legacyToast.classList.remove('show') };
    }

    const container = ensureToastContainer();

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast toast-enhanced ${type}`;

    // Icon
    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'toast-icon';
    iconWrapper.appendChild(createToastIcon(type));

    // Content
    const content = document.createElement('div');
    content.className = 'toast-content';

    const messageEl = document.createElement('span');
    messageEl.className = 'toast-message';
    messageEl.textContent = message;
    content.appendChild(messageEl);

    // Action button
    if (action && onAction) {
        const actionBtn = document.createElement('button');
        actionBtn.className = 'toast-action';
        actionBtn.textContent = action;
        actionBtn.addEventListener('click', () => {
            onAction();
            dismissToast(toast);
        });
        content.appendChild(actionBtn);
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'toast-close';
    closeBtn.innerHTML = `<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/></svg>`;
    closeBtn.addEventListener('click', () => dismissToast(toast));

    // Progress bar (if not persistent)
    let progressBar = null;
    if (!persistent) {
        progressBar = document.createElement('div');
        progressBar.className = 'toast-progress';
        progressBar.innerHTML = '<div class="toast-progress-bar"></div>';
    }

    // Assemble toast
    toast.appendChild(iconWrapper);
    toast.appendChild(content);
    toast.appendChild(closeBtn);
    if (progressBar) toast.appendChild(progressBar);

    // Add to container
    container.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
        toast.classList.add('show');
        if (progressBar) {
            const bar = progressBar.querySelector('.toast-progress-bar');
            bar.style.animationDuration = `${duration}ms`;
            bar.classList.add('running');
        }
    });

    // Auto-dismiss
    let timeoutId = null;
    if (!persistent) {
        timeoutId = setTimeout(() => dismissToast(toast), duration);
    }

    // Pause on hover
    toast.addEventListener('mouseenter', () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (progressBar) {
            const bar = progressBar.querySelector('.toast-progress-bar');
            bar.style.animationPlayState = 'paused';
        }
    });

    toast.addEventListener('mouseleave', () => {
        if (!persistent) {
            timeoutId = setTimeout(() => dismissToast(toast), 2000);
            if (progressBar) {
                const bar = progressBar.querySelector('.toast-progress-bar');
                bar.style.animationPlayState = 'running';
            }
        }
    });

    return {
        dismiss: () => dismissToast(toast),
        element: toast
    };
}

/**
 * Dismiss a toast with animation
 */
function dismissToast(toast) {
    if (!toast || toast.classList.contains('dismissing')) return;

    toast.classList.add('dismissing');
    toast.classList.remove('show');

    toast.addEventListener('transitionend', () => {
        toast.remove();
    }, { once: true });

    // Fallback removal if transition doesn't fire
    setTimeout(() => toast.remove(), 400);
}

/**
 * Hide toast notification (legacy support)
 */
export function hideToast() {
    const toast = document.getElementById('toast');
    if (toast) {
        toast.classList.remove('show');
    }
}

/**
 * Convenience methods
 */
export const toast = {
    success: (message, options) => showToast(message, 'success', options),
    error: (message, options) => showToast(message, 'error', options),
    info: (message, options) => showToast(message, 'info', options),
    warning: (message, options) => showToast(message, 'warning', options)
};
